/**
 * Movement & Collision
 * Handles WASD movement with collision detection against walls and terrain.
 */

import type { GridMap, GridMapState, MoveDirection, CardinalDirection } from './types';
import { isBlocked, getEntity, moveEntity, coordKey } from './grid';
import { computeVisibility, DEFAULT_VISION_RADIUS } from './visibility';
import { updateFog } from './fog';

const MOVE_TO_DIR: Record<MoveDirection, CardinalDirection> = {
  up: 'N',
  down: 'S',
  left: 'W',
  right: 'E',
};

const MOVE_DELTA: Record<MoveDirection, { dx: number; dy: number }> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
};

/** Check if a move from (x,y) in the given direction is valid. */
export function canMove(map: GridMap, x: number, y: number, direction: MoveDirection, openDoors?: Set<string>): boolean {
  return !isBlocked(map, x, y, MOVE_TO_DIR[direction], openDoors);
}

/** Get all valid moves from a position. */
export function getValidMoves(map: GridMap, x: number, y: number, openDoors?: Set<string>): MoveDirection[] {
  return (['up', 'down', 'left', 'right'] as MoveDirection[]).filter(d => canMove(map, x, y, d, openDoors));
}

/**
 * Attempt to move the player. Returns new state if movement succeeds,
 * or the unchanged state if blocked. Recomputes visibility and fog.
 *
 * If the destination is a closed door, auto-opens it and moves through
 * in one step (door opens + player enters).
 */
export function tryMove(state: GridMapState, direction: MoveDirection): GridMapState {
  const player = getEntity(state.map, state.playerId);
  if (!player) return state;

  const delta = MOVE_DELTA[direction];
  const nx = player.x + delta.dx;
  const ny = player.y + delta.dy;

  let openDoors = state.openDoors;

  // Check if destination is a closed door — auto-open it (unless locked)
  const destCell = state.map.cells[ny]?.[nx];
  const doorKey = coordKey(nx, ny);
  if (destCell?.terrain === 'door' && !openDoors.has(doorKey)) {
    // Locked doors can't be opened by walking into them
    if (state.lockedDoors.has(doorKey)) return state;

    // Check the path isn't blocked by walls/bounds (ignoring the door)
    const dir = MOVE_TO_DIR[direction];
    const cell = state.map.cells[player.y]?.[player.x];
    if (!cell || cell.walls[dir] || !destCell) return state;

    // Auto-open the door
    openDoors = new Set(openDoors);
    openDoors.add(doorKey);
  }

  // Now check if move is valid with (potentially updated) openDoors
  if (!canMove(state.map, player.x, player.y, direction, openDoors)) {
    return state;
  }

  const newMap = moveEntity(state.map, state.playerId, nx, ny);
  const radius = player.lightRadius ?? DEFAULT_VISION_RADIUS;
  const visibility = computeVisibility(newMap, nx, ny, radius, openDoors);

  // Clone fog/light grids then update in place
  const fogGrid = state.fogGrid.map(row => [...row]);
  const lightGrid = state.lightGrid.map(row => [...row]);
  updateFog(fogGrid, lightGrid, visibility);

  return {
    ...state,
    map: newMap,
    openDoors,
    fogGrid,
    lightGrid,
    moveCount: state.moveCount + 1,
  };
}
